export type ResultPayload = string | null | undefined | unknown;

export interface ResultLike {
  result?: ResultPayload;
  cancelled?: boolean | string | number | null;
  status?: string | null;
}

function parseResultPayload(result: ResultPayload): unknown[] | null {
  if (result === null || result === undefined || result === '') {
    return null;
  }

  if (typeof result === 'string') {
    const trimmed = result.trim();
    if (!trimmed || trimmed === '[]' || trimmed === '[[]]' || trimmed === '[[],[]]') {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  if (Array.isArray(result)) {
    return result;
  }

  return null;
}

function isTruthyCancelledFlag(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === 't' || normalized === '1' || normalized === 'yes';
  }

  return false;
}

export function isResultCancelled(item?: ResultLike | null): boolean {
  if (!item) {
    return false;
  }

  if (isTruthyCancelledFlag(item.cancelled)) {
    return true;
  }

  const status = String(item.status ?? '').trim().toLowerCase();
  if (status === 'cancelled' || status === 'canceled') {
    return true;
  }

  // Upcoming-draw cards may omit result entirely; only infer from result when present.
  if (item.result === undefined) {
    return false;
  }

  const parsed = parseResultPayload(item.result);
  if (parsed === null) {
    return true;
  }

  if (parsed.length === 0) {
    return true;
  }

  return parsed.every((group) => Array.isArray(group) && group.length === 0);
}

export function parseMainNumbers(result: ResultPayload): number[] {
  const parsed = parseResultPayload(result);
  if (!parsed || !Array.isArray(parsed[0])) {
    return [];
  }

  return parsed[0].map(Number).filter(Number.isFinite);
}

export function parseBonusNumbers(result: ResultPayload): number[] {
  const parsed = parseResultPayload(result);
  if (!parsed || !Array.isArray(parsed[1])) {
    return [];
  }

  return parsed[1].map(Number).filter(Number.isFinite);
}

/** Active account theme overrides (from theme JSON, e.g. wsb). */
let themeGameFlagsBySlug: Record<string, string> = {};
let themeGameFlagsBySlugPrefix: Record<string, string> = {};

export function setThemeGameFlags(
  bySlug?: Record<string, string>,
  bySlugPrefix?: Record<string, string>
): void {
  themeGameFlagsBySlug = bySlug ?? {};
  themeGameFlagsBySlugPrefix = bySlugPrefix ?? {};
}

function resolveSlugFlag(gameSlug: string): string | undefined {
  const slug = gameSlug.toLowerCase();

  if (slug in themeGameFlagsBySlug) {
    return themeGameFlagsBySlug[slug];
  }

  for (const [prefix, flagFile] of Object.entries(themeGameFlagsBySlugPrefix)) {
    if (slug.startsWith(prefix.toLowerCase())) {
      return flagFile;
    }
  }

  return undefined;
}

export function hasGameFlag(
  gameLogo: string | null | undefined,
  gameSlug?: string | null | undefined
): boolean {
  if (gameSlug && resolveSlugFlag(gameSlug)) {
    return true;
  }
  return Boolean(gameLogo);
}

export function getGameLogoUrl(
  gameLogo: string | null | undefined,
  gameSlug?: string | null | undefined
): string {
  const slugFlag = gameSlug ? resolveSlugFlag(gameSlug) : undefined;
  if (slugFlag) {
    return `/img/flags/${slugFlag}`;
  }
  return `/img/flags/${gameLogo}`;
}

export function formatDrawTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr.replace(' ', 'T'));
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year}, ${hours}:${minutes}`;
}
